/**
 * ADR-0229 — shared agent-registry helpers.
 *
 * One module, two readers:
 *   - the edge matcher (`apps/edge/src/identity/match.ts`) — identifies the
 *     visitor on every observation;
 *   - the origin (`services/api`) — links `agent_observed` / `crawler_event`
 *     rows to the registry, scores verification, and validates ops-approved
 *     additions before they are written to the `AGENT_REGISTRY` KV.
 *
 * Everything here is pure: no I/O, no clocks. The data lives in
 * `agent-registry-data.ts`.
 *
 * Identity strength is NOT decided here. A UA match only says what the
 * visitor CLAIMS to be; verification (Web Bot Auth signature, operator-
 * published IP ranges) is evaluated by the origin and recorded beside the
 * match, never folded into it.
 */
import { BUNDLED_REGISTRY, SILL_INTERNAL_TOOLS } from './agent-registry-data.js';
export { BUNDLED_REGISTRY, SILL_INTERNAL_TOOLS } from './agent-registry-data.js';
/** Closed category list (runtime mirror of {@link AgentRegistryCategory}). */
export const AGENT_REGISTRY_CATEGORIES = [
    'ai_crawler',
    'ai_assistant',
    'search',
    'seo',
    'monitoring',
    'social_preview',
    'hostile',
    'internal',
];
/** Categories an ops reviewer may choose when approving a proposal. */
export const APPROVABLE_CATEGORIES = [
    'ai_crawler',
    'ai_assistant',
    'search',
    'seo',
    'monitoring',
    // ADR-0230 — link-preview bots (the AI classifier's `link_preview`).
    'social_preview',
    'hostile',
];
/**
 * Categories the Automatic mode may ever approve on its own. ADR-0230
 * (founder decision 2026-10-06) widens ADR-0229's list to every labelling
 * category: registration only LABELS traffic, it never makes a visit
 * Verified. `hostile` (and `internal`) always need a person.
 */
export const AUTO_APPROVABLE_CATEGORIES = [
    'ai_crawler',
    'ai_assistant',
    'search',
    'seo',
    'monitoring',
    'social_preview',
];
/**
 * Categories whose approval writes an `AGENT_REGISTRY` KV seed. `hostile`
 * is recorded at origin only: putting a scanner's pattern in the identity
 * registry would make it a "recognised agent" on every merchant dashboard.
 */
export const KV_WRITABLE_CATEGORIES = [
    'ai_crawler',
    'ai_assistant',
    'search',
    'seo',
    'monitoring',
    'social_preview',
];
export function isAgentRegistryCategory(v) {
    return typeof v === 'string' && AGENT_REGISTRY_CATEGORIES.includes(v);
}
/**
 * ADR-0060 Major 3 — KV-supplied pattern bounds. Shared so the origin
 * refuses at approval time exactly what the edge would drop at load.
 */
export const MAX_USER_AGENT_PATTERN_LENGTH = 256;
export const REDOS_PRONE_RE = /\((?:\?:)?[^)]*?[+*]\)[+*]/;
/** True when the matcher may use this seed for UA / client-hint matching. */
export function isUaMatchable(seed) {
    return seed.robots_txt_only !== true;
}
const compiledCache = new WeakMap();
function compiled(seeds) {
    const hit = compiledCache.get(seeds);
    if (hit !== undefined)
        return hit;
    const out = [];
    for (const seed of seeds) {
        if (!isUaMatchable(seed))
            continue;
        try {
            out.push({ seed, re: new RegExp(seed.user_agent_pattern), len: seed.user_agent_pattern.length });
        }
        catch {
            // A malformed pattern never matches; the rest of the set still does.
        }
    }
    compiledCache.set(seeds, out);
    return out;
}
/** Bundled agent ids — the set KV may never claim (ADR-0060 additive-only). */
export const BUNDLED_AGENT_IDS = new Set(BUNDLED_REGISTRY.map((s) => s.agent_id));
/**
 * Same precedence as the edge matcher (`apps/edge/src/identity/match.ts`):
 *
 *   1. BUNDLED seeds first — longest matching pattern among the bundled
 *      floor wins.
 *   2. Only when NO bundled seed matches, the longest matching non-bundled
 *      (KV / ops-approved) seed.
 *
 * ADR-0229 security review M2: with a single longest-wins pass a KV
 * pattern could out-length a bundled one on the same UA and steal its
 * identity (`\bspider-feedback\b` beating `\bBytespider\b` on the real
 * Bytespider UA). Bundled precedence makes the floor unstealable by KV,
 * whatever the pattern length. Robots-only seeds are skipped.
 *
 * Pass the SAME array instance on every call to reuse the compile cache.
 */
export function matchUserAgentToSeed(seeds, user_agent) {
    if (typeof user_agent !== 'string' || user_agent.length === 0)
        return null;
    let bundled;
    let other;
    for (const c of compiled(seeds)) {
        if (!c.re.test(user_agent))
            continue;
        if (BUNDLED_AGENT_IDS.has(c.seed.agent_id)) {
            if (bundled === undefined || c.len > bundled.len)
                bundled = c;
        }
        else if (other === undefined || c.len > other.len) {
            other = c;
        }
    }
    return (bundled ?? other)?.seed ?? null;
}
const ROBOTS_ONLY_SEEDS = BUNDLED_REGISTRY
    .filter((s) => s.robots_txt_only === true)
    .map((s) => ({ seed: s, re: new RegExp(s.user_agent_pattern, 'i') }));
/**
 * ADR-0229 — when the UA carries a robots.txt-only token (Google-Extended,
 * Applebot-Extended), return the seed it impersonates. No real crawler
 * sends these tokens as its User-Agent.
 */
export function robotsOnlyTokenClaim(user_agent) {
    if (typeof user_agent !== 'string' || user_agent.length === 0)
        return null;
    for (const r of ROBOTS_ONLY_SEEDS) {
        if (r.re.test(user_agent))
            return r.seed;
    }
    return null;
}
// ---------------------------------------------------------------------------
// Ops-approval guards (ADR-0229 §5). Server-side; the admin UI mirrors them
// for early feedback only.
// ---------------------------------------------------------------------------
/**
 * Product token an ops reviewer can approve: starts alphanumeric, then
 * letters, digits, `-` or `_`, 2–63 chars total. Dots are excluded so a
 * token never needs escaping inside a pattern.
 */
export const UA_TOKEN_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{1,62}$/;
const V_SEGMENT = '[A-Za-z0-9._-]{1,64}';
/** Build the only pattern shapes the approval path accepts. */
export function buildUaPattern(token, style) {
    switch (style) {
        case 'slash_version':
            return `\\b${token}/${V_SEGMENT}`;
        case 'space_or_slash_version':
            return `\\b${token}[ /]${V_SEGMENT}`;
        case 'word':
            return `\\b${token}\\b`;
    }
}
/**
 * Strict grammar for an approved pattern: exactly one of the three shapes
 * {@link buildUaPattern} emits. Linear-time by construction (a literal,
 * then a single bounded class or a boundary) — the ReDoS check is the
 * grammar itself; the generic shape regex and length cap run as well for
 * defence in depth.
 */
const APPROVED_PATTERN_RE = /^\\b([A-Za-z0-9][A-Za-z0-9_-]{1,62})(?:\/\[A-Za-z0-9\._-\]\{1,64\}|\[ \/\]\[A-Za-z0-9\._-\]\{1,64\}|\\b)$/;
/**
 * User agents an approved pattern must NOT match (too broad otherwise).
 * ADR-0229 security review M1(b): desktop + mobile browsers, in-app
 * browsers, headless / embedded engines. A pattern that fires on any of
 * these would turn human visitors into "agents" at the edge.
 */
export const BROWSER_UA_CORPUS = [
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36',
    'Mozilla/5.0 (Windows NT 6.1; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/109.0.0.0 Safari/537.36',
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.6 Safari/605.1.15',
    'Mozilla/5.0 (X11; Linux x86_64; rv:131.0) Gecko/20100101 Firefox/131.0',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:131.0) Gecko/20100101 Firefox/131.0',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.6 Mobile/15E148 Safari/604.1',
    'Mozilla/5.0 (iPad; CPU OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.6 Mobile/15E148 Safari/604.1',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) CriOS/129.0.6668.69 Mobile/15E148 Safari/604.1',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) FxiOS/131.0 Mobile/15E148 Safari/605.1.15',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) EdgiOS/129.0.2792.84 Version/17.0 Mobile/15E148 Safari/604.1',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 [FBAN/FBIOS;FBAV/482.0.0.38.104;FBBV/650000000]',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Instagram 350.0.0.0.0',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) GSA/330.0.0 Mobile/15E148 Safari/604.1',
    'Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Mobile Safari/537.36',
    'Mozilla/5.0 (Linux; Android 14; SM-S918B) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/26.0 Chrome/122.0.0.0 Mobile Safari/537.36',
    'Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Mobile Safari/537.36 EdgA/129.0.2792.84',
    'Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Mobile Safari/537.36 OPR/84.0.0.0',
    'Mozilla/5.0 (Linux; U; Android 10; en-US; RMX2020) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/100.0.4896.58 UCBrowser/13.6.0.1315 Mobile Safari/537.36',
    'Mozilla/5.0 (Linux; Android 14; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/129.0.0.0 Mobile Safari/537.36',
    'Mozilla/5.0 (Linux; Android 9; KFTRWI) AppleWebKit/537.36 (KHTML, like Gecko) Silk/129.1.1 like Chrome/129.0.0.0 Safari/537.36',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36 Edg/129.0.0.0',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36 OPR/114.0.0.0',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/128.0.0.0 YaBrowser/24.10.0.0 Safari/537.36',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36 Vivaldi/6.9.3447.54',
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36 Brave/129',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) HeadlessChrome/129.0.0.0 Safari/537.36',
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Slack/4.40.0 Chrome/128.0.6613.36 Electron/32.0.1 Safari/537.36',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64; Trident/7.0; rv:11.0) like Gecko',
    'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.1; Trident/6.0)',
    'Opera/9.80 (Windows NT 6.1) Presto/2.12.388 Version/12.18',
    // ADR-0229 re-review — in-app browsers, webviews and desktop-app shells.
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 MicroMessenger/8.0.50(0x1800322d) NetType/WIFI Language/zh_CN',
    'Mozilla/5.0 (Linux; Android 14; Pixel 8 Build/AP2A.240805.005; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/128.0.6613.127 Mobile Safari/537.36 [FB_IAB/FB4A;FBAV/482.0.0.48.86;]',
    'Mozilla/5.0 (Linux; Android 14; SM-S918B Build/UP1A.231005.007; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/128.0.6613.127 Mobile Safari/537.36 Instagram 350.0.0.40.98 Android (34/14; 480dpi; 1080x2340; samsung; SM-S918B; dm3q; qcom; en_US; 643260185)',
    'Mozilla/5.0 (Linux; U; Android 13; zh-cn; V2229A Build/TP1A.220624.014) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/109.0.5414.86 MQQBrowser/14.9 Mobile Safari/537.36',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 QQ/9.0.85.627 V1_IPH_SQ_9.0.85_1_APP_A Pixel/1179 MiniAppEnable SimpleUISwitch/0 StudyMode/0 CurrentMode/0 CurrentFontScale/1.000000 QQTheme/1000 AppId/537230561 Core/WKWebView Device/Apple(iPhone 15 Pro) NetType/WIFI QBWebViewType/1 WKType/1',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 SP-engine/3.14.0 main/1.0 baiduboxapp/13.62.0.10 (Baidu; P2 17.6) NABar/1.0',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Weibo (iPhone15,2__weibo__14.8.2__iphone__os17.6)',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 ChannelId(1) Ariver/1.1.0 AliApp(AP/10.6.20.6000) Nebula WK RVKType(0) AlipayDefined(nt:WIFI,ws:393|852|3.0) AlipayClient/10.6.20.6000 Language/zh-Hans Region/CN',
    'Mozilla/5.0 (Linux; U; Android 14; zh-CN; 23013RK75C Build/UKQ1.230804.001) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/100.0.4896.58 Quark/7.4.5.680 Mobile Safari/537.36',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0.0.0 Safari/537.36 Teams/24215.1007.3082.1590/49',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36 Zoom/6.2.0',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) discord/1.0.9163 Chrome/124.0.6367.243 Electron/30.2.0 Safari/537.36',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Safari Line/14.13.0',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 KAKAOTALK 10.9.0',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 Snapchat/13.07.0.33 (like Safari/8618.3.11.10.1, panda)',
    'Mozilla/5.0 (Linux; Android 14; SM-A546B Build/UP1A.231005.007; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/128.0.6613.127 Mobile Safari/537.36 musical_ly_2023605030 JsSdk/1.0 NetType/WIFI Channel/googleplay AppName/musical_ly app_version/36.5.3 ByteLocale/en ByteFullLocale/en Region/US AppId/1233 Spark/1.6.0.4-bugfix AppVersion/36.5.3 BytedanceWebview/d8a21c6',
    'Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 [Pinterest/iOS]',
    'Mozilla/5.0 (Linux; Android 14; Pixel 8 Build/AP2A.240805.005; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/128.0.6613.127 Mobile Safari/537.36 [Pinterest/Android]',
];
/**
 * ADR-0229 security review M1(b) — browser / platform / engine product
 * tokens. A candidate token equal (case-insensitively) to one of these is
 * refused for Automatic approvals: these appear in human browser UAs, so
 * registering one would relabel people as agents. Amendment 2026-10-07: a
 * staff approval gets a warning instead (the browser-corpus match stays a
 * hard refusal for everyone).
 */
export const BROWSER_TOKEN_DENYLIST = new Set([
    'mozilla', 'applewebkit', 'webkit', 'khtml', 'gecko', 'like', 'compatible', 'presto', 'trident', 'msie', 'blink',
    'chrome', 'chromium', 'crios', 'headlesschrome', 'safari', 'mobile', 'version', 'firefox', 'fxios', 'focus', 'klar',
    'edg', 'edge', 'edga', 'edgios', 'opr', 'opera', 'opt', 'opios', 'samsungbrowser', 'yabrowser', 'yandexbrowser',
    'ucbrowser', 'ucweb', 'qqbrowser', 'miuibrowser', 'huaweibrowser', 'vivaldi', 'brave', 'whale', 'puffin', 'silk',
    'electron', 'duckduckgo', 'ddg', 'gsa', 'fban', 'fbav', 'fbios', 'instagram', 'line', 'snapchat', 'tiktok', 'pinterest',
    // ADR-0229 re-review — in-app browsers / webviews / desktop-app shells.
    'micromessenger', 'wechat', 'fb_iab', 'fb4a', 'fbdv', 'fbsv', 'fbmd', 'mqqbrowser', 'qq', 'baiduboxapp', 'weibo',
    'alipayclient', 'quark', 'teams', 'zoom', 'discord', 'kakaotalk', 'musical_ly', 'bytedancewebview', 'bytelocale',
    'aweme', 'trill', 'nativehost', 'pinterest_app', 'twitter', 'twitterandroid', 'linkedinapp', 'messenger',
    'whatsapp', 'telegram', 'outlook', 'slack', 'skype',
    'windows', 'win64', 'wow64', 'x64', 'macintosh', 'intel', 'linux', 'x11', 'android', 'iphone', 'ipad', 'ipod', 'cpu',
    'ubuntu', 'fedora', 'cros', 'wv', 'dalvik', 'rv', 'kftrwi', 'arm', 'aarch64',
]);
export function isBrowserProductToken(token) {
    return BROWSER_TOKEN_DENYLIST.has(token.toLowerCase());
}
/**
 * ADR-0229 security review M2 — one or more documented sample UAs for
 * EVERY UA-matchable bundled seed (a test enforces full coverage and that
 * each sample resolves to its own seed). An approved pattern may not
 * match any of them.
 */
export const BUNDLED_SAMPLE_UAS_BY_ID = {
    agent_anthropic_claude: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; ClaudeBot/1.0; +claudebot@anthropic.com)'],
    agent_anthropic_claude_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Claude-User/1.0; +Claude-User@anthropic.com)', 'Claude-User (claude-code/2.1.284; +https://support.anthropic.com/)'],
    agent_anthropic_claude_searchbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Claude-SearchBot/1.0; +Claude-SearchBot@anthropic.com)'],
    agent_openai_gpt: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.4; +https://openai.com/gptbot'],
    agent_openai_chatgpt_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ChatGPT-User/1.0; +https://openai.com/bot'],
    agent_openai_chatgpt: ['ChatGPT/1.2025.112 (iOS 18.4; iPhone17,1; build 14915134)'],
    agent_openai_searchbot: ['Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36; compatible; OAI-SearchBot/1.4; +https://openai.com/searchbot'],
    agent_openai_adsbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; OAI-AdsBot/1.0; +https://openai.com/adsbot'],
    agent_perplexity: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; PerplexityBot/1.0; +https://perplexity.ai/perplexitybot)'],
    agent_perplexity_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Perplexity-User/1.0; +https://perplexity.ai/perplexity-user)'],
    agent_microsoft_bingbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; bingbot/2.0; +http://www.bing.com/bingbot.htm) Chrome/116.0.1938.76 Safari/537.36'],
    agent_meta_externalagent: ['meta-externalagent/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)'],
    agent_meta_externalfetcher: ['meta-externalfetcher/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)'],
    agent_meta_webindexer: ['meta-webindexer/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)'],
    agent_meta_externalads: ['meta-externalads/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)'],
    agent_duckduckgo_assistbot: ['DuckAssistBot/1.2; (+http://duckduckgo.com/duckassistbot.html)'],
    agent_mistral_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; MistralAI-User/1.0; +https://docs.mistral.ai/robots)'],
    agent_google_searchbot: ['Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)', 'Mozilla/5.0 (Linux; Android 6.0.1; Nexus 5X Build/MMB29P) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Mobile Safari/537.36 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)'],
    agent_google_image_bot: ['Googlebot-Image/1.0'],
    agent_google_video_bot: ['Googlebot-Video/1.0'],
    agent_google_inspection_tool: ['Mozilla/5.0 (compatible; Google-InspectionTool/1.0)'],
    agent_apple_applebot: ['Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_5) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Safari/605.1.15 (Applebot/0.1; +http://www.apple.com/go/applebot)'],
    agent_commoncrawl: ['CCBot/2.0 (https://commoncrawl.org/faq/)'],
    agent_reflection_bot: ['Mozilla/5.0 (compatible; Reflectionbot/1.0; +https://reflection.ai/bot)'],
    agent_exa_searchbot: ['Mozilla/5.0 (compatible; ExaSearchBot/1.0; +https://crawler.exa.ai/)'],
    agent_amazonbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Amazonbot/0.1; +https://developer.amazon.com/support/amazonbot) Chrome/119.0.6045.214 Safari/537.36'],
    agent_amazon_searchbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Amzn-SearchBot/0.1) Chrome/119.0.6045.214 Safari/537.36'],
    agent_amazon_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Amzn-User/0.1) Chrome/119.0.6045.214 Safari/537.36'],
    agent_bytespider: [
        'Mozilla/5.0 (compatible; Bytespider; https://zhanzhang.toutiao.com/) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.0.0 Safari/537.36',
        'Mozilla/5.0 (Linux; Android 5.0) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; Bytespider; spider-feedback@bytedance.com)',
    ],
    agent_linkup_bot: ['LinkupBot/1.0 (LinkupBot for web indexing; https://www.linkup.so/bot; bot@linkup.so)'],
    agent_parallel_shapbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ShapBot/0.1.0'],
    agent_parallel_shap_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; Shap-User/0.1.0'],
    agent_moonshot_kimibot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; KimiBot/1.0; +https://www.kimi.com/policies/kimi-crawlers'],
    agent_moonshot_kimi_searchbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; Kimi-SearchBot/1.0; +https://www.kimi.com/policies/kimi-crawlers'],
    agent_moonshot_kimi_user: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; Kimi-User/1.0; +https://www.kimi.com/policies/kimi-crawlers'],
    agent_you_youbot: ['Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; YouBot/1.0; +https://docs.you.com/youbot; env:prod) Chrome/120.0.0.0 Safari/537.36'],
    agent_x_twitterbot: ['Twitterbot/1.0'],
    agent_meta_facebook_external: ['facebookexternalhit/1.1 (+http://www.facebook.com/externalhit_uatext.php)'],
    agent_meta_facebot: ['Facebot'],
    agent_linkedin_bot: ['LinkedInBot/1.0 (compatible; Mozilla/5.0; Apache-HttpClient +http://www.linkedin.com)'],
    agent_slack_link_expander: ['Slackbot-LinkExpanding 1.0 (+https://api.slack.com/robots)', 'Slackbot 1.0 (+https://api.slack.com/robots)'],
    agent_discord_bot: ['Mozilla/5.0 (compatible; Discordbot/2.0; +https://discordapp.com)'],
    // 2026-10-11 seed audit (labels-review): vendor-documented UA strings.
    agent_petalbot: ['Mozilla/5.0 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)', 'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)'],
    agent_yandexbot: ['Mozilla/5.0 (compatible; YandexBot/3.0; +http://yandex.com/bots)', 'Mozilla/5.0 (compatible; YandexImages/3.0; +http://yandex.com/bots)'],
    agent_baiduspider: ['Mozilla/5.0 (compatible; Baiduspider/2.0; +http://www.baidu.com/search/spider.html)', 'Mozilla/5.0 (compatible; Baiduspider-render/2.0; +http://www.baidu.com/search/spider.html)'],
    agent_sogou: ['Sogou web spider/4.0(+http://www.sogou.com/docs/help/webmasters.htm#07)'],
    agent_duckduckbot: ['DuckDuckBot/1.1; (+http://duckduckgo.com/duckduckbot.html)'],
    agent_sill_test_fixture: ['SillTestFixture/1.0'],
    agent_sill_heartbeat: ['SillHeartbeat/1.0'],
    // ADR-0230 — Sill's own tools (one documented sample each).
    ...Object.fromEntries(SILL_INTERNAL_TOOLS.map((t) => [t.agent_id, [t.sample]])),
};
/** Flat list of every bundled sample (back-compat name). */
export const BUNDLED_SAMPLE_UAS = Object.values(BUNDLED_SAMPLE_UAS_BY_ID).flat();
/**
 * Validate an ops-supplied pattern before it can reach the KV writer.
 * `sample_ua` (optional) must be matched by the pattern — guards against
 * approving a pattern that would never fire.
 *
 * Amendment 2026-10-07 (ADR-0230): `opts.allow_browser_token` lets a
 * STAFF approval register a denylisted token (e.g. an in-app browser
 * name) that the browser-corpus check does not catch — the reviewer gets
 * a warning instead. The corpus check itself always stays hard.
 */
export function validateApprovedUaPattern(pattern, sample_ua, opts) {
    if (typeof pattern !== 'string' || pattern.length === 0)
        return { ok: false, reason: 'bad_shape' };
    if (pattern.length > MAX_USER_AGENT_PATTERN_LENGTH)
        return { ok: false, reason: 'too_long' };
    if (REDOS_PRONE_RE.test(pattern))
        return { ok: false, reason: 'redos_shape' };
    const m = APPROVED_PATTERN_RE.exec(pattern);
    if (m === null || m[1] === undefined)
        return { ok: false, reason: 'bad_shape' };
    if (isBrowserProductToken(m[1]) && opts?.allow_browser_token !== true)
        return { ok: false, reason: 'browser_token' };
    let re;
    try {
        re = new RegExp(pattern);
    }
    catch {
        return { ok: false, reason: 'does_not_compile' };
    }
    if (BROWSER_UA_CORPUS.some((ua) => re.test(ua)))
        return { ok: false, reason: 'matches_browser' };
    if (BUNDLED_SAMPLE_UAS.some((ua) => re.test(ua)))
        return { ok: false, reason: 'collides_with_bundled' };
    // The token itself must not be (a case variant of) a bundled token —
    // e.g. `gptbot` would shadow nothing at the edge (bundled wins on the
    // exact-case UA) but would mislabel a lower-cased spoof as a new agent.
    const token_lc = m[1].toLowerCase();
    for (const s of BUNDLED_REGISTRY) {
        const bt = /^(?:\\b|\^)([A-Za-z0-9_-]+)/.exec(s.user_agent_pattern)?.[1];
        if (bt !== undefined && bt.toLowerCase() === token_lc)
            return { ok: false, reason: 'collides_with_bundled' };
    }
    if (sample_ua !== undefined && !re.test(sample_ua))
        return { ok: false, reason: 'sample_not_matched' };
    return { ok: true, token: m[1] };
}
/**
 * KV agent-id shape: `agent_` + 1–5 lower-case alphanumeric segments,
 * ≤ 64 chars. Mirrors `TEST_AGENT_ID_RE` in the kv-writer (test-shaped
 * ids are refused there against the prod namespace; refused here always).
 */
const KV_AGENT_ID_RE = /^agent_[a-z0-9]{2,40}(?:_[a-z0-9]{1,40}){0,4}$/;
const TEST_SEGMENT_RE = /^agent_(?:[a-z0-9]+_)*(?:phase2_test|smoke|verify|test)(?:_|$)/;
export function validateKvAgentId(id) {
    if (typeof id !== 'string' || id.length > 64 || !KV_AGENT_ID_RE.test(id))
        return { ok: false, reason: 'bad_shape' };
    if (id.startsWith('agent_sill_') || id === 'agent_sill')
        return { ok: false, reason: 'reserved_prefix' };
    if (TEST_SEGMENT_RE.test(id))
        return { ok: false, reason: 'test_shaped' };
    if (BUNDLED_AGENT_IDS.has(id))
        return { ok: false, reason: 'bundled_id' };
    return { ok: true };
}
/** Suggest a KV id from a product token: `LinkupBot` → `agent_linkupbot`. */
export function suggestKvAgentId(token) {
    const slug = token.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 56);
    return `agent_${slug.length >= 2 ? slug : 'unknown'}`;
}
/**
 * Best-effort product token from a UA (the `compatible; Name/x` slot,
 * else the first token). Same rule as the crawl-event `inferBotName`.
 */
export function inferUaProductToken(ua) {
    const m = ua.match(/compatible;\s+(\w[\w.-]*)(?:\/[^\s;)]+)?[;\s)]/i);
    if (m?.[1] && !/^MSIE$|^Trident$/i.test(m[1]))
        return m[1];
    const first = ua.split(/[/\s;()]/)[0];
    if (first && first.length > 1 && !/^Mozilla|^Opera$/i.test(first))
        return first;
    return null;
}
/**
 * Stable fingerprint of a registry snapshot — origin stores it on each
 * linked `agent_observed` row so a registry change (new bundled entry,
 * new approval) re-links rows in bounded batches.
 */
export function registrySnapshotVersion(seeds) {
    // FNV-1a 32-bit over id|pattern|robots flag — cheap, deterministic, no
    // crypto dependency (this is a change detector, not an integrity check).
    let h = 0x811c9dc5;
    const text = seeds
        .map((s) => `${s.agent_id}|${s.user_agent_pattern}|${s.robots_txt_only === true ? 1 : 0}`)
        .sort()
        .join('\n');
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `r${h.toString(16).padStart(8, '0')}`;
}
export const TRUSTED_SIGNERS = [
    // Exa documents `ExaSearchBot`; allow future Exa-prefixed product tokens.
    { host: 'crawler.exa.ai', operator: 'Exa', docs_url: 'https://crawler.exa.ai/', allowed_token_re: /^Exa[A-Z][A-Za-z0-9-]{1,40}$/ },
    // You.com documents only `YouBot`.
    { host: 'you.com', operator: 'You.com', docs_url: 'https://you.com/docs/youbot', allowed_token_re: /^YouBot$/ },
    // Common Crawl documents only `CCBot`.
    { host: 'commoncrawl.org', operator: 'Common Crawl', docs_url: 'https://commoncrawl.org/ccbot', allowed_token_re: /^CCBot$/ },
];
export const KNOWN_PUBLIC_SIGNERS = [
    // OpenAI ChatGPT agent-mode. User-triggered fetchers send
    // `ChatGPT-User/*`, the mobile app sends `ChatGPT/*`, the training
    // crawler GPTBot ships from a different family of IPs and is NOT a
    // chatgpt.com-signed product — so the regex refuses GPTBot here.
    { host: 'chatgpt.com', display: 'ChatGPT', allowed_token_re: /^(?:ChatGPT|ChatGPT-User|OAI-SearchBot|OAI-AdsBot)[A-Za-z0-9/._-]*$/ },
    // Exa documents the `ExaSearchBot` family.
    { host: 'crawler.exa.ai', display: 'Exa', allowed_token_re: /^Exa[A-Z][A-Za-z0-9-]{1,40}$/ },
    // You.com documents only `YouBot`.
    { host: 'you.com', display: 'YouBot', allowed_token_re: /^YouBot$/ },
    // Common Crawl documents only `CCBot`.
    { host: 'commoncrawl.org', display: 'Common Crawl', allowed_token_re: /^CCBot$/ },
    // Yahoo Mail's URL preview (ecp.yusercontent.com) is deliberately NOT
    // listed: it ships a generic browser UA, so no token gate can tell its
    // real fetches from a borrowed crawler name, and any listed entry earns
    // the mint VERIFIED chip (security review 2026-10-11). Its signed
    // fetches render as "SIGNED · UNVETTED" with the signer named.
];
/** Case-insensitive lookup by signer host. Returns `null` when the host is unknown. */
export function knownPublicSignerFor(host) {
    if (typeof host !== 'string' || host.length === 0)
        return null;
    const h = host.trim().toLowerCase();
    return KNOWN_PUBLIC_SIGNERS.find((s) => s.host === h) ?? null;
}
/**
 * Returns the merchant-facing display name for a verified public signer
 * host ONLY when the UA product token matches the signer's documented
 * `allowed_token_re`. Returns `null` otherwise (unknown signer, no UA,
 * or signer-UA mismatch — the dashboard then renders a weaker state).
 *
 * This is the one the UI and the audit-bundle export must call;
 * `publicSignerDisplay` (host-only) is intentionally removed because
 * it was the attack surface for the security review's MINOR 2.
 */
export function publicSignerDisplayFor(host, user_agent) {
    const entry = knownPublicSignerFor(host);
    if (entry === null)
        return null;
    const token = inferUaProductToken(user_agent ?? '');
    if (token === null)
        return null;
    return entry.allowed_token_re.test(token) ? entry.display : null;
}
/** True when `token` is one the trusted signer's operator may ship. */
export function signerAllowsToken(signer, token) {
    return signer.allowed_token_re.test(token);
}
/**
 * Registrable domain (eTLD+1) — deliberately simple: the last two labels,
 * or three when the second-level label is a common public suffix part
 * (`co.uk`, `com.au`, …). Used only to compare a trusted signer with its
 * own docs URL, both of which are code-reviewed constants or ops input.
 */
export function registrableDomain(host) {
    const labels = host.toLowerCase().replace(/\.$/, '').split('.').filter((l) => l.length > 0);
    if (labels.length <= 2)
        return labels.join('.');
    const sld = labels[labels.length - 2] ?? '';
    const take = /^(?:co|com|net|org|gov|edu|ac)$/.test(sld) && (labels[labels.length - 1] ?? '').length === 2 ? 3 : 2;
    return labels.slice(-take).join('.');
}
/** The trusted signer entry for a host, only when its registrable domain
 *  matches its docs domain (defence against a mis-edited list). */
export function trustedSignerFor(host) {
    if (typeof host !== 'string')
        return null;
    const h = host.trim().toLowerCase();
    const t = TRUSTED_SIGNERS.find((x) => x.host === h);
    if (t === undefined)
        return null;
    let docs_host;
    try {
        const u = new URL(t.docs_url);
        if (u.protocol !== 'https:')
            return null;
        docs_host = u.hostname;
    }
    catch {
        return null;
    }
    return registrableDomain(docs_host) === registrableDomain(t.host) ? t : null;
}
// ---------------------------------------------------------------------------
// ADR-0230 §1 — Sill's own User-Agents.
// ---------------------------------------------------------------------------
/** Agent ids of Sill's own tools (category `internal`). */
export const SILL_INTERNAL_AGENT_IDS = new Set(SILL_INTERNAL_TOOLS.map((t) => t.agent_id));
const SILL_INTERNAL_RES = SILL_INTERNAL_TOOLS.map((t) => new RegExp(`^${t.token}/[A-Za-z0-9._-]{1,64}`));
/** True when the UA is one Sill's own tools send (a claim — see ADR-0230 §1). */
export function isSillInternalUserAgent(user_agent) {
    if (typeof user_agent !== 'string' || user_agent.length === 0)
        return false;
    return SILL_INTERNAL_RES.some((re) => re.test(user_agent));
}
export const SILL_FIRST_PARTY_SIGNER = {
    slug: 'sill-readiness',
    signer: 'edge.sill.so/agents/sill-readiness',
    operator_display: 'Sill',
    tokens: new Map(SILL_INTERNAL_TOOLS
        .filter((t) => t.signed === true)
        .map((t) => [t.token.toLowerCase(), `Sill · ${t.name}`])),
};
/** Lower-cased product tokens of every Sill tool (signing or not). */
export const SILL_INTERNAL_TOKENS_LOWER = new Set(SILL_INTERNAL_TOOLS.map((t) => t.token.toLowerCase()));
/**
 * May a Sill-hosted credential from `slug` vouch for `token`? Sill tool
 * names only ever through the first-party signer (security review L1:
 * legacy operator rows can still hold a now-reserved token).
 */
export function credentialMayClaimToken(slug, token) {
    return slug === SILL_FIRST_PARTY_SIGNER.slug || !SILL_INTERNAL_TOKENS_LOWER.has(token.toLowerCase());
}
/** First-party signer slugs (reserved; directory served from code). */
export const SILL_FIRST_PARTY_SIGNER_SLUGS = new Set([SILL_FIRST_PARTY_SIGNER.slug]);
/** True when a stored `signed_agent` is Sill's own first-party signer. */
export function isSillFirstPartySigner(signed_agent) {
    return typeof signed_agent === 'string' && signed_agent.trim().toLowerCase() === SILL_FIRST_PARTY_SIGNER.signer;
}
/**
 * Postgres regex (applied to `lower(trim(user_agent))`) for Sill's own
 * traffic, excluded from merchant analytics. Prefix-anchored like the
 * original rollup rule (`sill-<anything>/` covers every `Sill-…` tool and
 * the status monitor); the non-dashed tokens are listed explicitly.
 * `SillHeartbeat/` (the visible ADR-0176 smoke agent) is deliberately NOT
 * here: merchants who opted in are meant to see it.
 */
export const SILL_OWN_UA_SQL_RE = (() => {
    const extra = SILL_INTERNAL_TOOLS
        .map((t) => t.token.toLowerCase())
        .filter((t) => !t.startsWith('sill-'))
        .map((t) => `${t}/`);
    return `^(sill-[a-z0-9-]+/|${extra.join('|')})`;
})();
//# sourceMappingURL=agent-registry.js.map